<script setup lang="ts">
import { RouterLink } from 'vue-router';
import { ChevronDown } from 'lucide-vue-next';
import type { TopMenuLinkView } from '@/nav/menuModel';

defineProps<{
  topMenuLinks: TopMenuLinkView[];
  isSearchOpen: boolean;
  useDesktopMegaMenu: boolean;
  activeDesktopSectionKey: string | null;
  hasTopMenuChildren: (item: TopMenuLinkView) => boolean;
  hasActiveTopMenuChild: (item: TopMenuLinkView) => boolean;
  isActive: (url: string) => boolean;
}>();

defineEmits<{
  'nav-item-enter': [sectionKey: string];
  'nav-item-leave': [];
  'trigger-focus': [sectionKey: string];
  'nav-click': [];
}>();
</script>

<template>
  <nav
    class="side-nav__desktop-links"
    :class="{ 'side-nav__desktop-links--search-open': isSearchOpen }"
    :inert="isSearchOpen ? true : undefined"
    aria-label="Main navigation"
  >
    <template v-for="item in topMenuLinks" :key="item.sectionKey">
      <div
        v-if="hasTopMenuChildren(item)"
        class="side-nav__desktop-item"
        @mouseenter="$emit('nav-item-enter', item.sectionKey)"
        @mouseleave="$emit('nav-item-leave')"
      >
        <RouterLink
          :to="item.path"
          class="side-nav__desktop-nav-trigger"
          :class="{
            'side-nav__desktop-nav-trigger--active':
              isActive(item.path) || hasActiveTopMenuChild(item),
            'side-nav__desktop-nav-trigger--mega-open':
              useDesktopMegaMenu && activeDesktopSectionKey === item.sectionKey,
          }"
          :aria-expanded="activeDesktopSectionKey === item.sectionKey"
          aria-controls="desktop-mega-panel"
          @focus="$emit('trigger-focus', item.sectionKey)"
          @click="$emit('nav-click')"
        >
          <span class="side-nav__desktop-nav-label">{{ item.label }}</span>
          <ChevronDown class="side-nav__desktop-chevron" aria-hidden="true" />
        </RouterLink>
      </div>
      <RouterLink
        v-else
        :to="item.path"
        class="side-nav__desktop-single-link"
        :class="{ 'side-nav__desktop-single-link--active': isActive(item.path) }"
        :aria-current="isActive(item.path) ? 'page' : undefined"
        @click="$emit('nav-click')"
      >
        <span class="side-nav__desktop-nav-label">{{ item.label }}</span>
      </RouterLink>
    </template>
  </nav>
</template>
